import { useState } from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Card,
  CardBody,
  Progress,
  Collapse,
} from "reactstrap";
import Header from "../../components/Headers/Header";
import AdminNavbar from "../../components/Navbars/AdminNavbar/AdminNavbar";
import useDiagnostico from "../../context/hooks/useDiagnostico";
import { ConnectionQuality, ServerHealth } from "../../utils/diagnostico";

type Cor = "verde" | "amarelo" | "vermelho";

const CORES: Record<Cor, string> = {
  verde: "#2dce89",
  amarelo: "#fb6340",
  vermelho: "#f5365c",
};

const EMOJI: Record<Cor, string> = {
  verde: "🟢",
  amarelo: "🟡",
  vermelho: "🔴",
};

const CONEXAO_UI: Record<ConnectionQuality, { cor: Cor; titulo: string }> = {
  otima: { cor: "verde", titulo: "Sua conexão está ótima" },
  instavel: { cor: "amarelo", titulo: "Sua conexão está instável" },
  lenta: { cor: "vermelho", titulo: "Sua internet está lenta agora" },
};

const SERVIDOR_UI: Record<ServerHealth, { cor: Cor; titulo: string }> = {
  Normal: { cor: "verde", titulo: "Funcionando normalmente" },
  Degraded: { cor: "amarelo", titulo: "Mais lento que o normal" },
  Down: { cor: "vermelho", titulo: "Instável no momento" },
};

const Semaforo = ({ cor, titulo }: { cor: Cor; titulo: string }) => (
  <div className="text-center">
    <div
      style={{
        width: 90,
        height: 90,
        borderRadius: "50%",
        backgroundColor: CORES[cor],
        margin: "0 auto",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 42,
      }}
    >
      {EMOJI[cor]}
    </div>
    <h3 className="mt-3 mb-0">{titulo}</h3>
  </div>
);

const DiagnosticoConexao = () => {
  const { testando, estagio, resultado, erro, executarTeste } = useDiagnostico();
  const [mostrarDetalhes, setMostrarDetalhes] = useState(false);

  return (
    <>
      <Header />
      <AdminNavbar brandText={"Diagnóstico de Conexão"} search={false} titleSearch={""} />

      <Container className="mt-5">
        <Row className="justify-content-center">
          <Col lg="9">
            <Card className="shadow">
              <CardBody className="text-center py-5">
                <h1 className="mb-2">Teste a sua conexão</h1>
                <p className="text-muted mb-4">
                  Um clique verifica se a lentidão está na sua internet ou no nosso sistema.
                </p>

                <Button
                  size="lg"
                  disabled={testando}
                  onClick={executarTeste}
                  style={{ color: "#FFF", backgroundColor: "#585858", minWidth: 260 }}
                >
                  {testando ? "Testando..." : "Testar minha conexão"}
                </Button>

                {testando && (
                  <div className="mt-4">
                    <Progress animated value={estagio.progress} />
                    <p className="text-muted mt-2 mb-0">{estagio.label}</p>
                  </div>
                )}

                {erro && (
                  <div
                    className="mt-4 p-3"
                    style={{ backgroundColor: "#fdd", borderRadius: 8, color: "#911" }}
                  >
                    {erro}
                  </div>
                )}
              </CardBody>
            </Card>

            {resultado && (
              <Card className="shadow mt-4">
                <CardBody className="py-5">
                  <Row className="align-items-start">
                    <Col md="6" className="border-right mb-4 mb-md-0">
                      <p className="text-uppercase text-muted text-center mb-3">
                        Sua conexão
                      </p>
                      <Semaforo
                        cor={CONEXAO_UI[resultado.conexao].cor}
                        titulo={CONEXAO_UI[resultado.conexao].titulo}
                      />
                    </Col>
                    <Col md="6">
                      <p className="text-uppercase text-muted text-center mb-3">
                        Nosso servidor
                      </p>
                      <Semaforo
                        cor={SERVIDOR_UI[resultado.servidor].cor}
                        titulo={SERVIDOR_UI[resultado.servidor].titulo}
                      />
                    </Col>
                  </Row>

                  <div
                    className="mt-5 p-4 text-center"
                    style={{ backgroundColor: "#f6f9fc", borderRadius: 8, fontSize: 18 }}
                  >
                    {resultado.conclusao}
                  </div>

                  <div className="text-center mt-4">
                    <Button
                      color="link"
                      onClick={() => setMostrarDetalhes(!mostrarDetalhes)}
                    >
                      {mostrarDetalhes ? "Ocultar detalhes técnicos" : "Ver detalhes técnicos"}
                    </Button>
                  </div>

                  <Collapse isOpen={mostrarDetalhes}>
                    <Row className="text-center mt-3">
                      <Col xs="6" md="3" className="mb-3">
                        <h4 className="mb-0">{Math.round(resultado.metrics.latencyMs)} ms</h4>
                        <small className="text-muted">Resposta até o sistema</small>
                      </Col>
                      <Col xs="6" md="3" className="mb-3">
                        <h4 className="mb-0">{Math.round(resultado.metrics.jitterMs)} ms</h4>
                        <small className="text-muted">Estabilidade</small>
                      </Col>
                      <Col xs="6" md="3" className="mb-3">
                        <h4 className="mb-0">{resultado.metrics.downloadMbps.toFixed(1)} Mbps</h4>
                        <small className="text-muted">Download até o sistema</small>
                      </Col>
                      <Col xs="6" md="3" className="mb-3">
                        <h4 className="mb-0">{resultado.metrics.uploadMbps.toFixed(1)} Mbps</h4>
                        <small className="text-muted">Upload até o sistema</small>
                      </Col>
                    </Row>
                    <p className="text-center text-muted mb-0" style={{ fontSize: 12 }}>
                      Estas medidas são entre o seu dispositivo e o nosso sistema — não é um teste de
                      velocidade de internet comum, então os números podem diferir de sites como o Speedtest.
                    </p>
                  </Collapse>
                </CardBody>
              </Card>
            )}
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default DiagnosticoConexao;
